import React, { Fragment, useState, useMemo } from 'react';
import Link from 'shared/SlashLink';
import usePageSeo from '../../hooks/usePageSeo';
import JsonLd, { webAppSchema } from 'shared/JsonLd';
import Header from '../../components/header/Header';
import PageTitle from '../../components/pagetitle/PageTitle';
import Scrollbar from '../../components/scrollbar/scrollbar';
import Footer from '../../components/footer/Footer';
import CtaSection from '../../components/CtaSection/CtaSection';
import ToolLeadCapture from '../../components/ToolLeadCapture/ToolLeadCapture';

// Weighted yes/no controls. Weights sum to 100. Each "fix" is shown when the
// control is answered "No" so the result doubles as a prioritized punch list.
const CONTROLS = [
    { id: 'mfa', weight: 18, q: 'Is multi-factor authentication (MFA) enforced on email, VPN, and admin accounts?', fix: 'Turn on MFA everywhere — it blocks the large majority of account-takeover attacks.' },
    { id: 'backups', weight: 16, q: 'Do you have automated, tested backups stored offsite or immutable (3-2-1 rule)?', fix: 'Implement automated 3-2-1 backups and test a restore quarterly — your last line of defense against ransomware.' },
    { id: 'edr', weight: 14, q: 'Is managed endpoint protection (EDR/antivirus) installed and monitored on every device?', fix: 'Deploy managed EDR with 24/7 monitoring on all endpoints and servers.' },
    { id: 'patching', weight: 12, q: 'Are operating systems and applications patched on a regular, enforced schedule?', fix: 'Establish an automated patch-management cadence for OS and third-party apps.' },
    { id: 'training', weight: 12, q: 'Do employees get regular security-awareness and phishing training?', fix: 'Run quarterly security-awareness training plus simulated phishing tests.' },
    { id: 'firewall', weight: 10, q: 'Is there a business-grade firewall with content filtering and intrusion prevention?', fix: 'Install a next-gen firewall (UTM/IPS) and segment guest, IoT, and staff networks.' },
    { id: 'access', weight: 8, q: 'Is access granted on least-privilege, with offboarding that revokes accounts promptly?', fix: 'Adopt least-privilege roles and a documented joiner/mover/leaver process.' },
    { id: 'ir', weight: 6, q: 'Do you have a written incident-response plan and know who to call after a breach?', fix: 'Document an incident-response plan with roles, contacts, and a breach playbook.' },
    { id: 'mdm', weight: 4, q: 'Are mobile devices and laptops encrypted and centrally managed (MDM)?', fix: 'Enforce full-disk encryption and enroll devices in mobile device management.' },
];

const CyberSecurityScorecardPage = () => {
    usePageSeo({
        title: 'Free Cyber Security Assessment — 2-Minute Scorecard',
        description: 'Free cyber security assessment for small business. Answer 9 questions to get a 0–100 score and a prioritized fix list — MFA, backups, EDR, and training.',
        keywords: 'cyber security scorecard, security assessment, small business cybersecurity, MFA, ransomware protection, security audit, managed IT security California, BUILDLAB',
    });

    // null = unanswered, true = yes, false = no
    const [answers, setAnswers] = useState(() => Object.fromEntries(CONTROLS.map((c) => [c.id, null])));

    const set = (id, val) => setAnswers((a) => ({ ...a, [id]: val }));

    const { score, answered, band, bandColor, gaps } = useMemo(() => {
        let earned = 0;
        let answeredCount = 0;
        const gapsList = [];
        CONTROLS.forEach((c) => {
            if (answers[c.id] === true) earned += c.weight;
            if (answers[c.id] !== null) answeredCount += 1;
            if (answers[c.id] === false) gapsList.push(c);
        });
        const s = Math.round(earned);
        let b = 'At risk', col = '#FF8A8A';
        if (s >= 85) { b = 'Strong'; col = '#7CFFB2'; }
        else if (s >= 65) { b = 'Good, with gaps'; col = '#BFF59A'; }
        else if (s >= 40) { b = 'Needs attention'; col = '#FFC9A3'; }
        return { score: s, answered: answeredCount, band: b, bandColor: col, gaps: gapsList };
    }, [answers]);

    const allAnswered = answered === CONTROLS.length;

    return (
        <Fragment>
            <Header />
            <main id="main-content" className="page_content">
                <PageTitle pageTitle={'Free Cyber Security Assessment (Scorecard)'} pagesub={'Scorecard'} pageTop={'Business IT'} />
                <JsonLd data={webAppSchema({ name: 'Cyber Security Scorecard', description: 'Free cyber security assessment for small business — a 0–100 score and prioritized fix list in 2 minutes.', path: '/cyber-security-scorecard' })} />
                <section className="section_space bg-light">
                    <div className="container">
                        <div className="row mb-4">
                            <div className="col-lg-9">
                                <h2 className="mb-3">How secure is your business? Find out in 2 minutes.</h2>
                                <p>
                                    Answer the nine questions below honestly. We'll calculate a <strong>0–100 security score</strong>
                                    {' '}weighted by real-world risk, and turn anything you answer "No" into a prioritized fix list.
                                    No email required — your answers stay in your browser.
                                </p>
                            </div>
                        </div>

                        <div className="row">
                            {/* Questions */}
                            <div className="col-lg-7 mb-4">
                                <div style={{ background: '#fff', border: '1px solid var(--bs-border-color)', borderRadius: 16, padding: '8px 28px 16px' }}>
                                    {CONTROLS.map((c, i) => (
                                        <div key={c.id} style={{ padding: '16px 0', borderBottom: i < CONTROLS.length - 1 ? '1px solid var(--bs-border-color)' : 'none' }}>
                                            <p className="mb-2" style={{ fontSize: '.95rem', fontWeight: 600 }}>{c.q}</p>
                                            <div style={{ display: 'flex', gap: 10 }}>
                                                <button
                                                    type="button"
                                                    onClick={() => set(c.id, true)}
                                                    className={`btn btn-sm ${answers[c.id] === true ? 'btn-primary' : 'btn-outline-secondary'}`}
                                                    aria-pressed={answers[c.id] === true}
                                                >Yes</button>
                                                <button
                                                    type="button"
                                                    onClick={() => set(c.id, false)}
                                                    className={`btn btn-sm ${answers[c.id] === false ? 'btn-primary' : 'btn-outline-secondary'}`}
                                                    aria-pressed={answers[c.id] === false}
                                                >No</button>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>

                            {/* Score */}
                            <div className="col-lg-5 mb-4">
                                <div style={{ background: 'var(--bs-dark)', borderRadius: 16, padding: '28px 30px', color: '#fff', position: 'sticky', top: 100 }}>
                                    <div style={{ fontSize: '.85rem', opacity: 0.8 }}>Your security score</div>
                                    <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, margin: '2px 0 6px' }}>
                                        <span style={{ fontSize: '3rem', fontWeight: 800, lineHeight: 1, color: bandColor }}>{score}</span>
                                        <span style={{ fontSize: '1.1rem', opacity: 0.7 }}>/ 100</span>
                                    </div>
                                    <div style={{ fontWeight: 700, color: bandColor, marginBottom: 6 }}>{band}</div>
                                    {!allAnswered && (
                                        <p style={{ fontSize: '.82rem', opacity: 0.75 }}>
                                            {answered} of {CONTROLS.length} answered — finish for an accurate score.
                                        </p>
                                    )}
                                    {gaps.length > 0 && (
                                        <div style={{ marginTop: 10 }}>
                                            <div style={{ fontSize: '.85rem', opacity: 0.85, marginBottom: 6 }}>Top priorities:</div>
                                            <ul style={{ paddingLeft: 18, margin: 0, fontSize: '.84rem', opacity: 0.92 }}>
                                                {gaps.slice(0, 4).map((g) => <li key={g.id} style={{ marginBottom: 6 }}>{g.fix}</li>)}
                                            </ul>
                                        </div>
                                    )}
                                    {allAnswered && gaps.length === 0 && (
                                        <p style={{ fontSize: '.88rem', opacity: 0.92 }}>
                                            Excellent — you have the core controls in place. We can validate them with a deeper audit
                                            and keep them monitored.
                                        </p>
                                    )}
                                    <Link to="/contact" className="btn btn-primary w-100 mt-3" style={{ display: 'block', textAlign: 'center' }}>
                                        <span className="btn_label" data-text="Get a free security review">Get a free security review</span>
                                    </Link>
                                    <ToolLeadCapture
                                        tool="Cyber Security Scorecard"
                                        summary={`Security score ${score}/100 (${band}) · ${gaps.length} gap(s) flagged${gaps.length ? ': ' + gaps.map((g) => g.id).join(', ') : ''}`}
                                    />
                                </div>
                            </div>
                        </div>

                        <p className="small text-muted mt-3" style={{ maxWidth: 880 }}>
                            This scorecard is a high-level self-assessment based on widely recognized controls (aligned with the CIS
                            Critical Security Controls and NIST CSF). It is not a substitute for a full risk assessment or penetration
                            test. BUILDLAB provides managed security, monitoring, and compliance services for California businesses.
                        </p>

                        <p className="small mt-3" style={{ maxWidth: 880 }}>
                            Close the gaps with <Link to="/it-services">managed cyber security</Link>, or estimate your spend with the{' '}
                            <Link to="/it-cost-calculator">managed IT cost calculator</Link>.
                        </p>
                    </div>
                </section>
                <CtaSection />
            </main>
            <Footer />
            <Scrollbar />
        </Fragment>
    );
};

export default CyberSecurityScorecardPage;
